import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Button } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFlask,
  faCompressArrowsAlt,
} from '@fortawesome/free-solid-svg-icons';
import { useAggregationConfig } from '../../../../hooks/SimViewer/useAggregationConfig';
import { useMapVisibility } from '../../../../hooks/SimViewer/useMapVisibility';
import type { Legend } from '../../../../types/common';
import { toCamel } from '../../../../utils/case';
import { includes } from '../../../../utils/array';
import { EyeIconButton } from '../../../EyeIconButton';
import { ColorSwitcher } from './ColorSwitcher';
import { AggregationTypeSwitcher } from './AggregationTypeSwitcher';
import { GridHeatmapConfig } from './GridHeatmapConfig';
import { OdMapConfig } from './OdMapConfig';
import { ScaleSwitcher } from './ScaleSwitcher';
import { LegendList } from './LegendList';
import { LegendEditActions } from './LegendEditActions';

type Props = {
  legends: Record<string, Legend[]>;
  menuOpen: boolean;
  toggleMenuOpen: () => void;
};

const ResultSection = ({
  legends,
  menuOpen,
  toggleMenuOpen,
}: Props): ReactNode => {
  const { t } = useTranslation();
  const { aggregationConfig } = useAggregationConfig();
  const { isAggregationVisible, setIsAggregationVisible } = useMapVisibility();

  if (!menuOpen) {
    return (
      <Button
        size="large"
        icon={<FontAwesomeIcon icon={faFlask} size="lg" color="#707070" />}
        onClick={toggleMenuOpen}
      />
    );
  }

  return (
    <Section>
      <SectionHeader>
        <FontAwesomeIcon icon={faFlask} size="lg" color="#707070" />
        <HeaderLabel>{t('analysisResults')}</HeaderLabel>
        <HeaderAction>
          <Button
            icon={
              <FontAwesomeIcon
                size="sm"
                icon={faCompressArrowsAlt}
                color="#9D9D9D"
              />
            }
            type="text"
            shape="circle"
            size="small"
            onClick={toggleMenuOpen}
          />
        </HeaderAction>
      </SectionHeader>
      {!aggregationConfig && (
        <EmptyState>{t('message.noAggregationTarget')}</EmptyState>
      )}
      {aggregationConfig && (
        <SectionItem>
          <SectionItemLabel>{t('aggregationTarget')}</SectionItemLabel>
          <SectionItemContent>
            {t(toCamel(aggregationConfig.target), {
              defaultValue: aggregationConfig.target,
            })}
          </SectionItemContent>
        </SectionItem>
      )}
      {aggregationConfig && (
        <SectionItem>
          <SectionItemLabel>{t('displayAs')}</SectionItemLabel>
          <SectionItemContent>
            <ColumnContainer>
              <AggregationTypeSwitcher />
              {aggregationConfig.type === 'grid_heatmap' && (
                <GridHeatmapConfig />
              )}
              {aggregationConfig.type === 'od_map' && <OdMapConfig />}
            </ColumnContainer>
          </SectionItemContent>
        </SectionItem>
      )}
      {includes(
        ['grid_heatmap', 'heatmap', 'areas', 'area_pref', 'links', 'arcs'],
        aggregationConfig?.type
      ) && (
        <SectionItem>
          <SectionItemLabel>{t('color')}</SectionItemLabel>
          <SectionItemContent>
            <ColorSwitcher
              count={legends[aggregationConfig.type]?.length ?? 0}
            />
          </SectionItemContent>
        </SectionItem>
      )}
      {includes(
        ['grid_heatmap', 'areas', 'area_pref', 'links'],
        aggregationConfig?.type
      ) &&
        legends[aggregationConfig.type]?.length > 0 && (
          <SectionItem>
            <SectionItemLabel>{t('scale')}</SectionItemLabel>
            <SectionItemContent>
              <ScaleSwitcher legends={legends[aggregationConfig.type]} />
            </SectionItemContent>
          </SectionItem>
        )}
      {aggregationConfig && legends[aggregationConfig.type]?.length > 0 && (
        <SectionItem>
          <SectionItemLabel full>
            <RowContainer>
              <span>{t('legend')}</span>
              {includes(
                ['grid_heatmap', 'areas', 'area_pref', 'links'],
                aggregationConfig.type
              ) && <LegendEditActions />}
              <EyeIconButton
                active={isAggregationVisible}
                onClick={(): void => {
                  setIsAggregationVisible(!isAggregationVisible);
                }}
              />
            </RowContainer>
          </SectionItemLabel>
          <SectionItemContent>
            <LegendList legends={legends[aggregationConfig.type] ?? []} />
          </SectionItemContent>
        </SectionItem>
      )}
    </Section>
  );
};

const Section = styled.div`
  width: 100%;
  background: #fafafa;
  padding: 0 0 8px 0;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: start;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-bottom: 1px solid #d9d9d9;
`;

const HeaderLabel = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  font-size: 16px;
  line-height: 16px;
`;

const HeaderAction = styled.div`
  margin-left: auto;
`;

const SectionItem = styled.div`
  margin: 8px 8px 0 8px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
`;

const SectionItemLabel = styled.div<{ full?: boolean }>`
  line-height: 1.3;
  font-size: 12px;
  font-weight: 600;
  flex: ${(props) => (props.full ? '1 1 100%' : '0 1 auto')};
`;

const SectionItemContent = styled.div`
  flex: 1 1 auto;
  min-width: 50%;
  text-align: right;
`;

const ColumnContainer = styled.div`
  min-width: 200px;
  width: fit-content;
  margin-left: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const RowContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: end;
  align-items: center;
  gap: 2px;

  & > :first-child {
    margin-right: auto;
  }
`;

const EmptyState = styled.p`
  font-size: 12px;
  color: #8c8c8c;
  margin: 4px 0 4px 16px;
`;

export { ResultSection };
